import type { FC, KeyboardEvent } from 'react';
import {
  getAccessibleTabIndex,
  getAriaButtonRoleAttribute,
  handleAccessibleButtonKeyDown,
} from '../../Button/ariaHelpers';
import { useConditionalClickHandler } from '../../hooks/useConditionalClickHandler';
import { fromRenderAsToElement } from '../../utils/InteractiveAtomProps';
import { listItemVariants } from './ListItem.styles';
import {
  type ListItemProps,
  isListItemWithCustomContent,
} from './ListItem.types';

export const ListItem: FC<ListItemProps> = (props) => {
  const {
    className,
    customContent,
    dataTestId,
    description,
    disabled,
    elementProps,
    endAdornment,
    forwardRef,
    label,
    onClick,
    onMouseDown,
    renderAs = 'div',
    selected,
    size,
    startAdornment,
  } = props;

  const interactive = onClick !== undefined;
  const listItemWithCustomContent = isListItemWithCustomContent(props);

  const handleClick = useConditionalClickHandler<
    HTMLDivElement | HTMLAnchorElement | HTMLButtonElement
  >(disabled, onClick);

  const DynamicComponent: any = fromRenderAsToElement[renderAs];

  // map your renderAs -> tv `as` variant
  const asVariant =
    renderAs === 'anchor'
      ? 'a'
      : renderAs === 'link'
        ? 'link'
        : renderAs === 'button'
          ? 'button'
          : 'div';

  const {
    root,
    content,
    label: labelSlot,
    description: descriptionSlot,
  } = listItemVariants({
    kind: listItemWithCustomContent ? 'custom' : 'list',
    size,
    selected,
    disabled,
    as: asVariant,
  });

  return (
    <DynamicComponent
      {...elementProps}
      data-testid={dataTestId}
      href={props.renderAs === 'anchor' ? props.href : undefined}
      className={root({ class: className })}
      ref={forwardRef}
      onClick={handleClick}
      onMouseDown={onMouseDown}
      {...getAriaButtonRoleAttribute(interactive)}
      tabIndex={getAccessibleTabIndex(!interactive || disabled)}
      onKeyDown={(
        e: KeyboardEvent<HTMLDivElement> | KeyboardEvent<HTMLAnchorElement>,
      ) => {
        if (!interactive) {
          handleAccessibleButtonKeyDown(e, disabled);
        }
      }}
    >
      {listItemWithCustomContent ? (
        <div className={content()}>{customContent}</div>
      ) : (
        <>
          {startAdornment}
          <div className={content()}>
            <span className={labelSlot()}>{label}</span>
            <span className={descriptionSlot()}>{description}</span>
          </div>
          {endAdornment}
        </>
      )}
    </DynamicComponent>
  );
};
